import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Checkbox } from 'components/ui/checkbox';
import { 
  ArrowLeft, Pill, Plus, Clock, Check, X, AlertTriangle, 
  RefreshCw, Calendar, Loader2, Edit, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const FREQUENCIES = [
  { value: 'once_daily', label: 'Once daily' },
  { value: 'twice_daily', label: 'Twice daily' },
  { value: 'three_times_daily', label: 'Three times daily' },
  { value: 'four_times_daily', label: 'Four times daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'as_needed', label: 'As needed' },
];

export default function Medications() {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: 'once_daily',
    schedule_times: ['08:00'],
    instructions: '',
    prescriber: '',
    pharmacy: '',
    quantity_remaining: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [medsRes, scheduleRes, refillsRes] = await Promise.all([
        api.get(`/medications`),
        api.get(`/medications/today`),
        api.get(`/medications/refills`)
      ]);
      setMedications(medsRes.data.medications || []);
      setSchedule(scheduleRes.data.schedule || []);
      setRefills(refillsRes.data.refills_needed || []);
    } catch (error) {
      console.error('Failed to fetch medications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/medications`, {
        ...formData,
        quantity_remaining: formData.quantity_remaining ? parseInt(formData.quantity_remaining) : null
      });
      toast.success('Medication added!');
      setShowAdd(false);
      setFormData({
        name: '', dosage: '', frequency: 'once_daily', schedule_times: ['08:00'],
        instructions: '', prescriber: '', pharmacy: '', quantity_remaining: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const handleLogDose = async (medId, scheduledTime, status) => {
    try {
      await api.post(`/medications/${medId}/log`, {
        status,
        scheduled_time: scheduledTime
      });
      toast.success(status === 'taken' ? 'Marked as taken!' : 'Marked as skipped');
      fetchData();
    } catch (error) {
      toast.error('Failed to log dose');
    }
  };

  const handleDeleteMedication = async (medId) => {
    if (!confirm('Remove this medication?')) return;
    try {
      await api.delete(`/medications/${medId}`);
      toast.success('Medication removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove medication');
    }
  };

  const addScheduleTime = () => {
    setFormData(prev => ({
      ...prev,
      schedule_times: [...prev.schedule_times, '12:00']
    }));
  };

  const updateScheduleTime = (index, value) => {
    setFormData(prev => ({
      ...prev,
      schedule_times: prev.schedule_times.map((t, i) => i === index ? value : t)
    }));
  };

  const removeScheduleTime = (index) => {
    setFormData(prev => ({
      ...prev,
      schedule_times: prev.schedule_times.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="medications-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Medications</h1>
              <p className="text-xs sm:text-sm text-slate-500">Track and manage medications</p>
            </div>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button data-testid="add-medication-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Medication
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Medication</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddMedication} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Medication Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Lisinopril"
                      required
                      data-testid="med-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dosage *</Label>
                    <Input
                      value={formData.dosage}
                      onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                      placeholder="e.g., 10mg"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select value={formData.frequency} onValueChange={(v) => {
                    const defaults = {
                      once_daily: ['08:00'],
                      twice_daily: ['08:00', '20:00'],
                      three_times_daily: ['08:00', '14:00', '20:00'],
                      four_times_daily: ['08:00', '12:00', '16:00', '20:00'],
                      every_other_day: ['08:00'],
                      weekly: ['08:00'],
                      as_needed: [],
                    };
                    setFormData({ ...formData, frequency: v, schedule_times: defaults[v] || ['08:00'] });
                  }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCIES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Schedule Times</Label>
                  <div className="space-y-2">
                    {formData.schedule_times.map((time, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Input
                          type="time"
                          value={time}
                          onChange={(e) => updateScheduleTime(idx, e.target.value)}
                          className="flex-1"
                        />
                        {formData.schedule_times.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeScheduleTime(idx)}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={addScheduleTime}>
                      <Plus className="w-4 h-4 mr-1" /> Add Time
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Instructions</Label>
                  <Input
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    placeholder="e.g., Take with food"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prescriber</Label>
                    <Input
                      value={formData.prescriber}
                      onChange={(e) => setFormData({ ...formData, prescriber: e.target.value })}
                      placeholder="Dr. Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity Remaining</Label>
                    <Input
                      type="number"
                      value={formData.quantity_remaining}
                      onChange={(e) => setFormData({ ...formData, quantity_remaining: e.target.value })}
                      placeholder="30"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                  <Button type="submit">Add Medication</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Refill Alerts */}
        {refills.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                Refills Needed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {refills.map((r) => (
                  <div key={r.medication_id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                    <div>
                      <p className="font-medium text-amber-900">{r.name}</p>
                      <p className="text-sm text-amber-700">{r.reason}</p>
                    </div>
                    {r.pharmacy && <Badge variant="secondary">{r.pharmacy}</Badge>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="today" className="space-y-4">
          <TabsList>
            <TabsTrigger value="today">Today's Schedule</TabsTrigger>
            <TabsTrigger value="all">All Medications</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-4">
            {schedule.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Pill className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No medications scheduled for today</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {schedule.map((item, idx) => (
                  <Card key={idx} className={item.status === 'taken' ? 'opacity-60' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            item.status === 'taken' ? 'bg-green-100' :
                            item.status === 'missed' ? 'bg-rose-100' : 'bg-teal-100'
                          }`}>
                            {item.status === 'taken' ? (
                              <Check className="w-6 h-6 text-green-600" />
                            ) : item.status === 'missed' ? (
                              <X className="w-6 h-6 text-rose-600" />
                            ) : (
                              <Pill className="w-6 h-6 text-teal-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{item.medication_name}</h4>
                            <p className="text-sm text-slate-500">{item.dosage}</p>
                            {item.instructions && (
                              <p className="text-xs text-slate-400 mt-1">{item.instructions}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-lg font-semibold text-slate-900">{item.scheduled_time}</p>
                            {item.status !== 'pending' && (
                              <Badge variant={item.status === 'taken' ? 'default' : 'destructive'}>
                                {item.status}
                              </Badge>
                            )}
                          </div>
                          {item.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleLogDose(item.medication_id, item.scheduled_time, 'taken')}
                                className="bg-green-500 hover:bg-green-600"
                                data-testid={`take-${item.medication_id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleLogDose(item.medication_id, item.scheduled_time, 'skipped')}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4">
            {medications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Pill className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No medications added yet</p>
                  <Button className="mt-4" onClick={() => setShowAdd(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Medication
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {medications.map((med) => (
                  <Card key={med.medication_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <Pill className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{med.name}</h4>
                            <p className="text-sm text-slate-500">{med.dosage}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              {FREQUENCIES.find(f => f.value === med.frequency)?.label || med.frequency}
                            </p>
                            {med.schedule_times?.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {med.schedule_times.map((t, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteMedication(med.medication_id)}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {med.quantity_remaining !== null && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm text-slate-500">
                            <span className={med.quantity_remaining <= 7 ? 'text-amber-600 font-medium' : ''}>
                              {med.quantity_remaining} doses remaining
                            </span>
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
